import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CaseMasterSignupDialog from 'dialogs/CaseMasterSignupDialog';

const useStyles = makeStyles((theme) => ({
  pricingContainer: {
    width: '100%',
    backgroundColor: '#F9FAFB',
    padding: theme.spacing(20, 37.5),
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
      // The header is sticky below md; keep #pricing from landing under it.
      scrollMarginTop: '80px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  contentWrapper: {
    maxWidth: '1200px',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(15),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(8),
    },
  },
  subHeading: {
    ...theme.typography['h6-regular'],
    color: '#71717A',
    maxWidth: '700px',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: {
      ...theme.typography['body01-regular'],
    },
  },
  cardsContainer: {
    display: 'flex',
    gap: theme.spacing(6),
    justifyContent: 'center',
    marginBottom: theme.spacing(12),
    paddingTop: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(4),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(6),
      marginBottom: theme.spacing(8),
      paddingTop: theme.spacing(5),
    },
  },
  pricingCard: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: `1px solid #E4E4E7`,
    height: 'fit-content',
    borderRadius: '16px',
    padding: theme.spacing(8, 6),
    minWidth: '320px',
    maxWidth: '360px',
    position: 'relative',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    [theme.breakpoints.down('lg')]: {
      minWidth: '280px',
      maxWidth: '320px',
      padding: theme.spacing(6, 5),
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
      minWidth: 'unset',
      maxWidth: '400px',
      padding: theme.spacing(6, 5),
    },
  },
  popularCard: {
    border: `2px solid #11154F`,
    background: `linear-gradient(135deg, #E2EEFF 0%, #FFF 100%)`,
    boxShadow: `0 7.909px 11.863px -2.373px rgba(0, 0, 0, 0.10), 0 3.163px 4.745px -3.163px rgba(0, 0, 0, 0.10)`,
  },
  popularBadge: {
    position: 'absolute',
    top: '-16px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#11154F',
    color: theme.palette.shades['clr-white-900'],
    ...theme.typography['body02-semiBold'],
    padding: theme.spacing(1, 3),
    borderRadius: '20px',
    whiteSpace: 'nowrap',
    zIndex: 10,
  },
  priceNoteText: {
    ...theme.typography['body02-regular'],
    color: '#D14343',
    marginTop: theme.spacing(1),
  },
  popularPrice: {
    color: '#0051FF',
  },
  cardHeader: {
    marginBottom: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  planName: {
    ...theme.typography['body02-semiBold'],
    color: theme.palette.neutral['clr-900'],
    marginBottom: theme.spacing(2),
  },
  priceBlock: {
    marginBottom: theme.spacing(3),
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    // Struck-through price + price + unit can outgrow a narrow card.
    flexWrap: 'wrap',
  },
  price: {
    ...theme.typography['h4-bold'],
    color: theme.palette.neutral['clr-900'],
  },
  originalPrice: {
    ...theme.typography['h6-regular'],
    color: theme.palette.neutral['clr-500'],
    textDecoration: 'line-through',
  },
  priceUnit: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-600'],
  },
  planDescription: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-600'],
    marginBottom: theme.spacing(6),
    lineHeight: 1.6,
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down('md')]: {
      gap: theme.spacing(2.5),
      marginBottom: theme.spacing(6),
    },
  },
  featureItem: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  checkIcon: {
    color: '#10B981',
    fontSize: '20px',
    marginTop: '2px',
    flexShrink: 0,
  },
  featureText: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-700'],
    flex: 1,
  },
  getStartedButton: {
    width: '100%',
    backgroundColor: theme.palette.neutral['clr-900'],
    color: theme.palette.shades['clr-white-900'],
    ...theme.typography['h6-medium'],
    padding: theme.spacing(3, 4),
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.neutral['clr-800'],
    },
  },
  popularButton: {
    backgroundColor: '#0A3F63',
    '&:hover': {
      backgroundColor: '#074060',
    },
  },
  enterpriseSection: {
    backgroundColor: theme.palette.shades['clr-white-900'],
    border: `1px solid ${theme.palette.neutral['clr-200']}`,
    borderRadius: '16px',
    padding: theme.spacing(6, 8),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '900px',
    margin: '0 auto',
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(5, 6),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(4),
      alignItems: 'flex-start',
      padding: theme.spacing(5, 5),
    },
  },
  enterpriseContent: {
    flex: 1,
  },
  enterpriseTitle: {
    ...theme.typography['h5-bold'],
    color: theme.palette.neutral['clr-900'],
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      ...theme.typography['h6-bold'],
    },
  },
  enterpriseDescription: {
    ...theme.typography['body01-regular'],
    color: theme.palette.neutral['clr-600'],
  },
  contactButton: {
    backgroundColor: theme.palette.neutral['clr-900'],
    color: theme.palette.shades['clr-white-900'],
    ...theme.typography['body01-bold'],
    padding: theme.spacing(2.5, 6),
    borderRadius: '8px',
    textTransform: 'none',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: theme.palette.neutral['clr-800'],
    },
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}));

const CasePricing = () => {
  const classes = useStyles();

  const [openSignupDialog, setOpenSignupDialog] = React.useState(false);

  const pricingPlans = [
    {
      name: 'Free Trial',
      price: null,
      currency: '',
      unit: '',
      priceNote: null,
      description: 'Experience how CaseMaster AI helps you solve a real business case and gives instant feedback on your approach.',
      features: [
        '1 free case to practise',
        'Detailed AI feedback for your solved case',
        'Score based on your case-solving approach',
        'Understand your strengths and improvement areas',
        'Preview how CaseMaster AI helps you prepare for consulting, product, strategy, and business roles',
      ],
      isPopular: false,
      planType: 'free',
    },
    {
      name: 'The Interview Pro',
      price: '4,000',
      originalPrice: '6,000',
      currency: '₹',
      unit: '/ 6 months',
      priceNote: 'Price increases by ₹1,000 next month',
      description: '',
      features: [
        'Unlimited access to AI-powered case interviews, HR interviews, and verbal mock interviews',
        'Unlimited access to Shortlisting Genie to optimize your resume and maximize your chances of getting shortlisted',
        'Unlimited access to the AI Resume Generator to create professional, ATS-friendly resumes',
        'Access to an exclusive WhatsApp community of serious MBB and FAANG aspirants taking the 30 Cases in 30 Days Challenge',
        'Dedicated Case Buddy for 1:1 practice and accountability for 1 year and weekly peer-led case practice sessions',
        'Access to job and internship boards',
        '1:1 expert sessions and Product Sense 101 workshop with Lokap Sahu, Founder with 12+ years of experience in Product and Analytics',
        '1:1 expert session with Prafull Agarwal, HR Leader with 10+ years of experience',
        'AI Prototyping and MVP workshop by Shraddha, IIT Bombay 2017, InMobi',
        'Prompt Engineering workshop by Vandana, Product Manager at Languify',
      ],
      isPopular: true,
      planType: 'paid',
      razorpayLink: process.env.REACT_APP_RAZORPAY_CASE_B2C_PRO_PLAN_LINK,
    },
    {
      name: 'Pinnacle Mentorship Plan',
      price: '30,000',
      originalPrice: '40,000',
      currency: '₹',
      unit: 'one-time',
      priceNote: null,
      description: '',
      features: [
        'Includes everything in the Interview Pro plan',
        "1:1 mentorship with Founder Lokap Sahu, bringing 12+ years of experience across Product Management, Founder's Office, and Data Analytics",
        'Unlimited resume reviews and personalized improvement guidance from Lokap',
        'Unlimited HR and behavioural interview practice sessions with Lokap',
        'Opportunity to work on live, industry-relevant projects',
        'Letter of Recommendation and completion certificate, subject to successful completion',
        "Referral and networking support through Lokap's professional network for relevant internship and job opportunities",
        'Personalized case interview preparation roadmap',
        'Expert guidance for consulting, product, strategy, analytics, and business roles',
        'Priority support and access to premium preparation resources',
      ],
      isPopular: false,
      planType: 'paid',
      razorpayLink: process.env.REACT_APP_RAZORPAY_CASE_B2C__PREMIUM_PLAN_LINK,
    },
  ];

  const handlePlanClick = (plan) => {
    if (plan.planType === 'free') {
      setOpenSignupDialog(true);
    } else if (plan.razorpayLink) {
      // Paid plans redirect to their Razorpay payment link; the backend
      // webhook provisions the account and emails credentials after payment.
      window.open(plan.razorpayLink, '_self');
    }
  };

  return (
    <Box className={classes.pricingContainer} id="pricing">
      <Box className={classes.contentWrapper}>
        {/* Header */}
        <Box className={classes.header}>
          <Typography variant='h2-bold' color='#2E3558' mb={2}>
            Ready to nail your upcoming case interviews?
          </Typography>
          <Typography className={classes.subHeading}>
            Practice real interview scenarios, get personalized feedback, and walk into your next interview with certainty.
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Box className={classes.cardsContainer}>
          {pricingPlans.map((plan, index) => (
            <Box
              key={index}
              className={`${classes.pricingCard} ${plan.isPopular ? classes.popularCard : ''}`}
            >
              {plan.isPopular && (
                <Box className={classes.popularBadge}>Most Popular</Box>
              )}

              <Box className={classes.cardHeader}>
                <Typography className={classes.planName}>{plan.name}</Typography>
              </Box>

              {plan.price && (
                <Box className={classes.priceBlock}>
                  <Box className={classes.priceContainer}>
                    {plan.originalPrice && (
                      <Typography className={classes.originalPrice}>
                        {plan.currency}{plan.originalPrice}
                      </Typography>
                    )}
                    <Typography className={`${classes.price} ${plan.isPopular ? classes.popularPrice : ''}`}>
                      {plan.currency}{plan.price}
                    </Typography>
                    <Typography className={classes.priceUnit}>{plan.unit}</Typography>
                  </Box>
                  {plan.priceNote && (
                    <Typography className={classes.priceNoteText}>{plan.priceNote}</Typography>
                  )}
                </Box>
              )}

              {plan.description && (
                <Typography className={classes.planDescription}>
                  {plan.description}
                </Typography>
              )}

              <Box className={classes.featuresList}>
                {plan.features.map((feature, idx) => (
                  <Box key={idx} className={classes.featureItem}>
                    <CheckCircleIcon className={classes.checkIcon} />
                    <Typography className={classes.featureText}>{feature}</Typography>
                  </Box>
                ))}
              </Box>

              <Button
                className={`${classes.getStartedButton} ${plan.isPopular ? classes.popularButton : ''}`}
                onClick={() => handlePlanClick(plan)}
              >
                Get started
              </Button>
            </Box>
          ))}
        </Box>

        {/* Enterprise Section */}
        <Box className={classes.enterpriseSection}>
          <Box className={classes.enterpriseContent}>
            <Typography className={classes.enterpriseTitle}>
              Enterprise & Universities
            </Typography>
            <Typography className={classes.enterpriseDescription}>
              Custom solutions for teams and institutions
            </Typography>
          </Box>
          <Button
            className={classes.contactButton}
            onClick={() => window.open(process.env.REACT_APP_GET_IN_TOUCH_TALLY_FORM_URL, '_blank', 'noopener,noreferrer')}
          >
            Contact us
          </Button>
        </Box>
      </Box>

      {/* Free-plan signup dialog */}
      <CaseMasterSignupDialog
        open={openSignupDialog}
        onClose={() => setOpenSignupDialog(false)}
      />
    </Box>
  );
};

export default CasePricing;
